import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import {
  Bell,
  BellOff,
  CheckCheck,
  Info,
  AlertTriangle,
  CreditCard,
  Megaphone,
  AlertCircle,
  Sparkles,
  type LucideIcon,
} from 'lucide-react'
import { apiService } from '@/services/apiService'
import type { Notification, NotificationType } from '@/types'

interface NotificationPanelProps {
  open: boolean
  onClose: () => void
}

const TYPE_ICON: Record<NotificationType, LucideIcon> = {
  INFO: Info,
  WARNING: AlertTriangle,
  ERROR: AlertCircle,
  SUCCESS: Sparkles,
  PAYMENT: CreditCard,
  ANNOUNCEMENT: Megaphone,
}

const TYPE_TONE: Record<NotificationType, string> = {
  INFO: 'bg-lakeblue/10 text-lakeblue',
  WARNING: 'bg-gold/15 text-copper',
  ERROR: 'bg-copper/10 text-copper',
  SUCCESS: 'bg-meadow/15 text-meadow',
  PAYMENT: 'bg-primary/10 text-primary',
  ANNOUNCEMENT: 'bg-gold/15 text-copper',
}

const formatRelative = (iso: string) => {
  const ms = Date.now() - new Date(iso).getTime()
  const m = Math.floor(ms / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 7) return `${d}d ago`
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * Notifications dropdown panel anchored under the header bell. Pulls live
 * data from `/notifications`, supports mark-as-read on click, and a
 * "mark all" action in the header.
 *
 * Premium calibration: list rows have a subtle left-border accent for unread,
 * empty state celebrates "you're all caught up" rather than feeling like a
 * void, and the footer offers a way out (Profile → notifications settings).
 */
const NotificationPanel = ({ open, onClose }: NotificationPanelProps) => {
  const qc = useQueryClient()
  const panelRef = useRef<HTMLDivElement>(null)

  const { data: list = [], isLoading } = useQuery<Notification[]>({
    queryKey: ['notifications'],
    queryFn: () => apiService.getNotifications(),
    enabled: open,
    staleTime: 30 * 1000,
  })

  const markRead = useMutation({
    mutationFn: (id: string) => apiService.markNotificationAsRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const markAll = useMutation({
    mutationFn: () => apiService.markAllNotificationsAsRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const unreadCount = list.filter((n) => !n.isRead).length

  // Click outside / escape close
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    const onClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    // Defer so the click that opened us doesn't immediately close.
    setTimeout(() => document.addEventListener('mousedown', onClick), 0)
    return () => {
      window.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClick)
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-12 w-[22rem] sm:w-96 bg-paper rounded-2xl shadow-float border border-mist/40 overflow-hidden z-50 animate-fade-in origin-top-right"
      role="dialog"
      aria-label="Notifications"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-mist/40">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-primary" />
          <h3 className="font-serif text-sm font-bold text-deepwater">Notifications</h3>
          {unreadCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-copper text-white text-[0.65rem] font-semibold">
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={() => markAll.mutate()}
            disabled={markAll.isLoading}
            className="text-[0.65rem] uppercase tracking-eyebrow font-semibold text-primary hover:text-deepwater inline-flex items-center gap-1 disabled:opacity-50"
          >
            <CheckCheck className="w-3 h-3" />
            Mark all read
          </button>
        )}
      </div>

      <div className="max-h-[26rem] overflow-y-auto">
        {isLoading ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex gap-3 animate-pulse">
                <div className="w-8 h-8 rounded-full bg-mist/40 shrink-0" />
                <div className="flex-1 space-y-2 pt-1">
                  <div className="h-2.5 bg-mist/40 rounded-md w-2/3" />
                  <div className="h-2 bg-mist/30 rounded-md w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : list.length === 0 ? (
          <div className="py-10 px-6 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-meadow/10 ring-1 ring-meadow/20 flex items-center justify-center mb-3">
              <BellOff className="w-5 h-5 text-meadow" />
            </div>
            <p className="font-serif text-sm font-semibold text-deepwater">All caught up</p>
            <p className="text-xs text-slate mt-1">
              Nothing new from the lake. We&rsquo;ll let you know when there is.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-mist/30">
            {list.map((n) => {
              const Icon = TYPE_ICON[n.type] ?? Info
              return (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => !n.isRead && markRead.mutate(n.id)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors hover:bg-mist/20 ${
                      !n.isRead ? 'border-l-2 border-copper bg-paper-fade/40' : 'border-l-2 border-transparent'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${TYPE_TONE[n.type]}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-deepwater truncate">{n.title}</p>
                        <span className="text-[0.65rem] text-slate whitespace-nowrap">{formatRelative(n.createdAt)}</span>
                      </div>
                      <p className="text-xs text-slate mt-0.5 line-clamp-2 text-pretty">{n.message}</p>
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {list.length > 0 && (
        <div className="px-4 py-2.5 border-t border-mist/40 bg-paper-fade text-center">
          <Link
            to="/profile"
            onClick={onClose}
            className="text-[0.7rem] uppercase tracking-eyebrow font-semibold text-primary hover:text-deepwater"
          >
            Notification preferences
          </Link>
        </div>
      )}
    </div>
  )
}

export default NotificationPanel
